import StructClient from '../client/struct/StructClient';
import GatewayContainer from '../config/GatewayContainer';
import GatewayNames from '../constant/GatewayNames';
import { DataStructure } from '../domain/structure/DataStructureDomains';
import MessageUtil from '../util/MessageUtil';
import LanguageUtil from '../util/LanguageUtil';
import React from 'react';
import { EnrichedComponentCategory } from '../domain/category/CategoryOperationDomains';
import FolderClient from '../client/folder/FolderClient';
import ComponentName from '../constant/ComponentName';
import { dispatch } from '../redux/store';
import { openBulkActionDialogFromDependency } from '../redux/actions/BulkActionActions';
import { query } from '@trlogic/client-js';
import SelectQuery = query.SelectQuery;
import { changeStructDirty } from '../redux/actions/StructActions';
import DeployErrorUtil from '../util/DeployErrorUtil';

class StructService {
  private structClient: StructClient = GatewayContainer.getInstance().get(GatewayNames.STRUCT_CLIENT);
  private folderClient: FolderClient = GatewayContainer.getInstance().get(GatewayNames.FOLDER_CLIENT);

  public async save(struct: DataStructure, isUpdate: boolean, folder: EnrichedComponentCategory) {
    try {
      if (struct.name != undefined && struct.name.trim().length > 0) {
        const isExist = await this.structClient.exist(struct.name);
        if (isExist && !isUpdate) {
          MessageUtil.warning({
            summary: LanguageUtil.get({ id: 'common.label.struct', defaultMessage: 'Struct' }),
            detail: (
              <div>
                <b>{struct.name}</b> {LanguageUtil.get({ id: 'messages.warning.structNameExist', defaultMessage: 'struct name exist' })}
              </div>
            ),
          });
          return false;
        }
        await this.structClient.save(struct);
        if (!isUpdate && folder) {
          await this.folderClient.addItemToCategory(ComponentName.STRUCT, folder.id, struct.name);
        }
        MessageUtil.success({
          summary: LanguageUtil.get({ id: 'common.label.struct', defaultMessage: 'Struct' }),
          detail: (
            <div>
              <b>{struct.name}</b> {LanguageUtil.get({ id: 'messages.save', defaultMessage: 'saved' })}
            </div>
          ),
        });
        dispatch(changeStructDirty(false));
        return true;
      } else {
        MessageUtil.warning({
          summary: LanguageUtil.get({ id: 'common.label.struct', defaultMessage: 'Struct' }),
          detail: (
            <div>
              <b>{struct.name}</b> {LanguageUtil.get({ id: 'messages.warning.undefined', defaultMessage: 'struct name can not be undefined' })}
            </div>
          ),
        });
      }
    } catch (e) {
      MessageUtil.error({
        summary: LanguageUtil.get({ id: 'common.label.struct', defaultMessage: 'Struct' }),
        detail: (
          <div>
            <b>{struct.name}</b> {LanguageUtil.get({ id: 'messages.warning.notSaved', defaultMessage: 'not saved' })}
          </div>
        ),
      });
      return false;
    }
  }

  public async remove(id: string): Promise<boolean> {
    try {
      await this.structClient.remove(id);
      MessageUtil.success({
        summary: LanguageUtil.get({ id: 'common.label.structVariable', defaultMessage: 'Struct Variable' }),
        detail: (
          <div>
            <b>{id}</b> {LanguageUtil.get({ id: 'messages.remove', defaultMessage: 'removed' })}
          </div>
        ),
      });
      return true;
    } catch (e: any) {
      if (DeployErrorUtil.show(e, LanguageUtil.get({ id: 'common.label.struct', defaultMessage: 'Struct' }))) {
        return false;
      }
      const dependencies: any = e.response.data;
      if (dependencies) {
        dispatch(openBulkActionDialogFromDependency(dependencies, ComponentName.STRUCT));
      }
      return false;
    }
  }

  public async removeAll(ids: string[]): Promise<boolean> {
    try {
      for (const id of ids) {
        await this.remove(id);
      }
      return true;
    } catch (e) {
      MessageUtil.error({
        content: LanguageUtil.get({ id: 'messages.warning.removeStructures', defaultMessage: 'Error while remove structures' }),
      });
      return false;
    }
  }

  public getCount(_query: SelectQuery): Promise<number> {
    return this.structClient.count(_query);
  }

  public getByIds(ids: string[]): Promise<DataStructure[]> {
    return this.structClient.getByIds(ids);
  }
}

export default StructService;
